import { enableProdMode, importProvidersFrom } from '@angular/core';
import { environment } from './environments/environment';
import { QmadsService } from './app/service/qmads.service';
import { ApiAddressService } from './app/service/api.address.service';
import { SysConfigService } from './app/service/sys.config';
import { LoginService } from './app/service/login.service';
import { DataLoaderService } from './app/service/data.loader.service';
import { DataLoader } from './app/service/data.loader';
import { QuoteService } from './app/service/quote.service';
import { QuoteStepClient } from './app/service/quote/quote.step.client';
import { QuoteStepBenefit } from './app/service/quote/quote.step.benefit';
import { QuoteStepCompare } from './app/service/quote/quote.step.compare';
import { QuoteStepCompareH2H } from './app/service/quote/quote.step.compare.h2h';
import { UserService } from './app/service/user.service';
import { UserSecurityService } from './app/service/user.security.service';
import { SharedFunctionService } from './app/service/shared.function.service';
import { ResizeService } from './app/service/screen.resize.service';
import { CanDeactivateGuard } from './app/tools/can-deactivate.guard';
import { ConfirmMessageDialogService } from './app/components/shared/confirm-message-dialog/confirm-message-dialog.service';
import { ApiService } from './app/service/api.service';
import { UserMenuService } from './app/service/user.menu.service';
import { DatePipe, CommonModule } from '@angular/common';
import { LegacyLeavingGuard } from './app/tools/legacy-leaving-guard.guard';
import { QprAdminService } from './app/service/qpr-admin.service';
import { AdminNoticesService } from './app/service/notice/admin-notices.service';
import {
  PreloadAllModules,
  provideRouter,
  UrlSerializer,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { LowerCaseUrlSerializer } from './app/tools/lower.case.url.serializer';
import { MAT_DATE_LOCALE, MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { TINYMCE_SCRIPT_SRC, EditorModule } from '@tinymce/tinymce-angular';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { CurrencyMaskModule } from 'ng2-currency-mask';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { A11yModule } from '@angular/cdk/a11y';
import { BidiModule } from '@angular/cdk/bidi';
import { ObserversModule } from '@angular/cdk/observers';
import { OverlayModule } from '@angular/cdk/overlay';
import { PlatformModule } from '@angular/cdk/platform';
import { PortalModule } from '@angular/cdk/portal';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      CommonModule,
      FormsModule,
      BrowserModule,
      ReactiveFormsModule,
      CurrencyMaskModule,
      MatAutocompleteModule,
      MatButtonModule,
      MatButtonToggleModule,
      MatCardModule,
      MatCheckboxModule,
      MatDatepickerModule,
      MatDialogModule,
      MatExpansionModule,
      MatFormFieldModule,
      MatGridListModule,
      MatIconModule,
      MatInputModule,
      MatListModule,
      MatMenuModule,
      MatNativeDateModule,
      MatPaginatorModule,
      MatProgressBarModule,
      MatProgressSpinnerModule,
      MatRadioModule,
      MatRippleModule,
      MatSelectModule,
      MatSidenavModule,
      MatSliderModule,
      MatSnackBarModule,
      MatSortModule,
      MatTableModule,
      MatTabsModule,
      MatTooltipModule,
      MatBadgeModule,
      A11yModule,
      BidiModule,
      ObserversModule,
      OverlayModule,
      PlatformModule,
      PortalModule,
      DragDropModule,
      MatSidenavModule,
      EditorModule,
    ),

    QmadsService,
    ApiAddressService,
    SysConfigService,
    LoginService,
    DataLoaderService,
    DataLoader,
    QuoteService,
    QuoteStepClient,
    QuoteStepBenefit,
    QuoteStepCompare,
    QuoteStepCompareH2H,
    UserService,
    UserSecurityService,
    SharedFunctionService,
    ResizeService,
    CanDeactivateGuard,
    ConfirmMessageDialogService,
    ApiService,
    UserMenuService,
    DatePipe,
    LegacyLeavingGuard,
    QprAdminService,
    AdminNoticesService,
    {
      provide: UrlSerializer,
      useClass: LowerCaseUrlSerializer,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'en-NZ',
    },
    {
      provide: TINYMCE_SCRIPT_SRC,
      useValue: 'tinymce/tinymce.min.js',
    },
    provideRouter(
      routes,
      // Preloading Strategy
      withPreloading(PreloadAllModules),
      // Initial Navigation (for SSR)
      withEnabledBlockingInitialNavigation(),
      // Scrolling Behavior (replaces scrollPositionRestoration and anchorScrolling)
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
      // For `useHash: true`, add `withHashLocation()`. The default behavior is to use the PathLocationStrategy (clean URLs). Only add withHashLocation() if want useHash: true.
      // For `enableTracing: true`, add `withDebugTracing()`. The default is not to trace. Only add the withDebugTracing() function when need to debug router events.
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
  ],
}).catch(err => console.error(err));
